import { useRouter } from 'next/router'
import { useQueryClient } from 'react-query'
import { useEffect, useMemo } from 'react'

import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { historyUtils } from '@wartek-id/toolbox-helper'
import { Button } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast'
import { Spinner } from '@wartek-id/spinner'

import { SKP_QUERY_KEY, useAPIKinerja } from 'api/kinerja/useAPIKinerjaKS'
import CardKinerjaSteps from 'app/KinerjaShared/components/CardKinerjaSteps'
import { RoleLabel, isKsDefinitifPlt } from 'app/KinerjaKSPage/utils/profile'
import CardKinerjaStatus from 'app/KinerjaKSPage/components/CardKinerjaStatus'
import SchoolList from 'app/KinerjaKSPage/components/School/SchoolList'
import { API_GET_SCHOOLS_QUERY_KEY } from 'api/kinerja/kepala-sekolah/school'
import { GeneralNoticeState } from 'components/NoticeState'
import {
  useApiDeletePelaksanaan,
  useApiDeleteSkp,
} from 'api/kinerja/kepala-sekolah/skp'
import useSchoolContext from 'app/KinerjaKSPage/hooks/useSchoolContext'
import {
  API_GET_KS_PELAKSANAAN_QUERY_KEY,
  useApiGetPelaksanaan,
} from 'api/kinerja/kepala-sekolah/pelaksanaan'
import useSKPKSPelaksanaanTrackEvent from 'app/KinerjaKSPage/hooks/useSKPKSPelaksanaanTrackEvent'
import KinerjaSidebar from 'app/KinerjaShared/components/KinerjaSidebar'
import { CardDataConfirmation } from 'app/KinerjaKSPage/components/CardDataConfirmation'
import CardOrganizationProgress from 'app/KinerjaKSPage/components/CardOrganizationProgress'
import {
  API_GET_KS_PENILAIAN_PROGRESS_QUERY_KEY,
  useApiGetPenilaianProgress,
} from 'api/kinerja/kepala-sekolah/penilaian'
import {
  API_GET_KS_RIWAYAT,
  API_GET_KS_RIWAYAT_DETAIL,
  API_GET_KS_RIWAYAT_HASIL_EVALUASI,
  API_GET_KS_RIWAYAT_HASIL_EVALUASI_TAHUNAN_DETAIL,
} from 'api/kinerja/kepala-sekolah/riwayat'
import BannerPengaliranEkin from 'app/KinerjaShared/components/BannerPengaliranEkin'
import useToggleSdkContext from 'utils/useToogleSdk'
import { BannerUpdatedTargetPerilaku } from 'app/KinerjaKSPage/components/BannerUpdatedTargetPerilaku'
import useResetToggle from 'app/KinerjaPage/hooks/useResetToggle'
import { usePengelolaanKinerjaBaseUrl } from 'app/KinerjaShared/hooks/usePengelolaanKinerjaBaseUrl'
import { PENGELOLAAN_KINERJA_KSPNF_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { isKsSPNFDefinitifPlt } from 'app/KinerjaKSPNFPage/utils/profile'

import type { TOrganizationProgressStep } from 'app/KinerjaKSPage/components/CardOrganizationProgress'

interface KinerjaKSIndexAppProps {}

const ResetButtons = () => {
  const { enabled: allowedToReset } = useResetToggle()

  const router = useRouter()
  const toast = useToast()
  const queryClient = useQueryClient()
  const baseUrl = usePengelolaanKinerjaBaseUrl()

  const { currentNpsn } = useSchoolContext()

  const { mutateAsync: resetSkpByNpsn, isLoading: isRemovingSkp } =
    useApiDeleteSkp(currentNpsn)

  const {
    mutateAsync: resetPelaksanaanByNpsn,
    isLoading: isRemovingPelaksanaan,
  } = useApiDeletePelaksanaan(currentNpsn)

  const invalidateQueries = async () => {
    const opts = {
      refetchActive: false,
      refetchInactive: false,
    }

    // Invalidate SKP query to clear cache and force to refetch
    await queryClient.invalidateQueries([SKP_QUERY_KEY, currentNpsn])

    // Invalidate school list query to clear cache and force to refetch
    await queryClient.invalidateQueries(API_GET_SCHOOLS_QUERY_KEY)

    // Remove pelaksanaan query to clear cached data
    queryClient.removeQueries([API_GET_KS_PELAKSANAAN_QUERY_KEY, currentNpsn])

    // Remove progress penilaian query to clear cached data
    queryClient.invalidateQueries([
      API_GET_KS_PENILAIAN_PROGRESS_QUERY_KEY,
      currentNpsn,
    ])

    // Invalidate riwayat-related queries, disable auto refetch as it's not needed, except for riwayat tahunan
    queryClient.invalidateQueries([API_GET_KS_RIWAYAT], opts)
    queryClient.invalidateQueries([API_GET_KS_RIWAYAT_DETAIL], opts)
    queryClient.invalidateQueries([API_GET_KS_RIWAYAT_HASIL_EVALUASI], opts)
    queryClient.invalidateQueries([
      API_GET_KS_RIWAYAT_HASIL_EVALUASI_TAHUNAN_DETAIL,
    ])
  }

  const resetSkp = async () => {
    if (allowedToReset !== true) return

    try {
      if (confirm('Apakah Anda yakin ingin mereset data SKP?')) {
        await resetSkpByNpsn(currentNpsn)

        await invalidateQueries()

        // go to homepage
        router.push(baseUrl)
      }
    } catch (error) {
      toast({
        message: 'Gagal mereset data SKP',
      })
    }
  }

  const resetDataPelaksanaan = async () => {
    if (allowedToReset !== true) return

    try {
      if (confirm('Apakah Anda yakin ingin mereset data pelaksanaan?')) {
        await resetPelaksanaanByNpsn(currentNpsn)

        await invalidateQueries()

        // go to homepage
        router.push(baseUrl)
      }
    } catch (error) {
      toast({
        message: 'Gagal mereset data pelaksanaan',
      })
    }
  }

  return (
    <div className="fixed bottom-0 right-0 p-4 z-50 flex gap-4">
      <Button
        size="sm"
        disabled={isRemovingSkp}
        variant="solid"
        color="red"
        onClick={resetSkp}
      >
        Reset Data Perencanaan (Internal Use)
      </Button>
      <Button
        size="sm"
        disabled={isRemovingPelaksanaan}
        variant="solid"
        color="red"
        onClick={resetDataPelaksanaan}
      >
        Reset Data Pelaksanaan (Internal Use)
      </Button>
    </div>
  )
}

const KinerjaKSIndexApp = ({}: KinerjaKSIndexAppProps) => {
  const router = useRouter()
  const { enabled: allowedToReset } = useResetToggle()
  const toggleValue = useToggleSdkContext()

  const { trackOpenKinerjaHomepage } = useSKPKSPelaksanaanTrackEvent()

  const {
    id: schoolId,
    profile,
    isProfileFetched,
    isAutoSelectSchool,
    schools,
    selectedSchool,
    currentNpsn,

    isLoadingSchools,
    isFailedLoadingSchools,
    refetchSchools,
  } = useSchoolContext()

  const baseUrl = usePengelolaanKinerjaBaseUrl()
  const isKsSpnf = baseUrl === PENGELOLAAN_KINERJA_KSPNF_PEGAWAI_PATH

  const {
    data,
    isFetched: isPerencanaanFetched,
    refetch: refetchSKP,
  } = useAPIKinerja(currentNpsn, {
    enabled: router.isReady && !!currentNpsn,
  })

  const {
    data: dataPelaksanaan,
    isLoading: isLoadingPelaksanaan,
    isSuccess: isSuccessPelaksanaan,
  } = useApiGetPelaksanaan({
    npsn: currentNpsn,
    opts: {
      useErrorBoundary: false,
      enabled: isPerencanaanFetched && data?.data?.status === 'READY',
    },
  })

  const {
    data: dataPenilaianProgress,
    isLoading: isLoadingPenilaianProgress,
    isSuccess: isSuccessPenilaianProgress,
  } = useApiGetPenilaianProgress(
    { npsn: currentNpsn },
    { enabled: isSuccessPelaksanaan }
  )

  useEffect(() => {
    if (data) {
      trackOpenKinerjaHomepage({
        kinerja_state:
          data?.data?.status === 'READY' ? 'pelaksanaan' : 'perencanaan',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const { showProgress, progressActiveStep } = useMemo(() => {
    const showProgress =
      isSuccessPelaksanaan &&
      ['REFLECTION_FOLLOW_UP_EVALUATION', 'COMPLETED'].includes(
        dataPelaksanaan?.data?.state
      )

    const progressData =
      !isLoadingPenilaianProgress && isSuccessPenilaianProgress
        ? dataPenilaianProgress?.data
        : null

    let progressActiveStep: TOrganizationProgressStep = 'STEP_PENILAIAN'
    if (progressData?.statePengiriman === 'PS_TO_DINAS')
      progressActiveStep = 'STEP_PENETAPAN_PS'
    else if (progressData?.statePengiriman === 'DINAS_TO_KS')
      progressActiveStep = 'STEP_PENETAPAN_DINAS'

    return { showProgress, progressActiveStep }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessPelaksanaan, isSuccessPenilaianProgress])

  const showBannerPengaliranSKP =
    toggleValue?.['kinerja-pengaliran-ekin-enabled'] &&
    (data?.data?.status === 'READY' ||
      data?.data?.status === 'WAITING_EVALUATION')

  const checkKSAccess = isKsSpnf ? isKsSPNFDefinitifPlt : isKsDefinitifPlt

  if (isProfileFetched && !profile?.ksAktif && !checkKSAccess(profile)) {
    router.push('/404')
  }

  if (!router.isReady) {
    return null
  }
  if (!isAutoSelectSchool) {
    return (
      <div className="pl-64">
        <KinerjaSidebar
          sticky
          activeMenu={isKsSpnf ? 'kepala-spnf-pegawai' : 'ks-pegawai'}
        />
        <div className="p-6 flex flex-col gap-6 w-full">
          {/* If have no school selected */}
          {!selectedSchool && !schoolId && (
            <>
              {/* Loading schools */}
              {isLoadingSchools && <SchoolList isLoading />}

              {/* Failed to load schools */}
              {!isLoadingSchools && isFailedLoadingSchools && (
                <div className="bg-white p-8 flex justify-center">
                  <div className="max-w-xl text-center">
                    <GeneralNoticeState
                      showButton
                      onButtonClick={refetchSchools}
                    />
                  </div>
                </div>
              )}

              {/* Only if no school is currently selected */}
              {!schoolId && schools && (
                <SchoolList
                  data={schools}
                  onClick={({ id }) => {
                    router.push(`${baseUrl}?id=${id}`)
                  }}
                />
              )}
            </>
          )}
          {/* If already have a school selected */}
          {!!selectedSchool && !!schoolId && (
            <>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <Icon
                    as="button"
                    onClick={() => historyUtils.backToApp(router.back)}
                  >
                    arrow_back
                  </Icon>
                  <Text variant="heading-md">
                    {selectedSchool?.schoolName} (
                    {RoleLabel?.[selectedSchool?.role]})
                  </Text>
                </div>
              </div>

              {showBannerPengaliranSKP && (
                <BannerPengaliranEkin jabatan="ks" hideRgtkColumn={true} />
              )}

              {/* Reset button */}
              {allowedToReset && <ResetButtons />}

              {isPerencanaanFetched && (
                <>
                  {showProgress && (
                    <CardOrganizationProgress activeStep={progressActiveStep} />
                  )}

                  <CardDataConfirmation npsn={currentNpsn} />

                  <BannerUpdatedTargetPerilaku />

                  <CardKinerjaStatus
                    skpId={data.data.id}
                    status={data.data.status}
                    onSuccessUpdateSKP={refetchSKP}
                    dataPelaksanaan={dataPelaksanaan?.data}
                  />
                  {isLoadingPelaksanaan && (
                    <div className="w-full flex justify-center">
                      <Spinner />
                    </div>
                  )}
                </>
              )}

              <CardKinerjaSteps />
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="pl-64">
      <KinerjaSidebar
        sticky
        activeMenu={isKsSpnf ? 'kepala-spnf-pegawai' : 'ks-pegawai'}
      />
      <div className="p-6 flex flex-col gap-6 w-full">
        {isPerencanaanFetched && (
          <>
            {showBannerPengaliranSKP && (
              <BannerPengaliranEkin jabatan="ks" hideRgtkColumn={true} />
            )}

            {allowedToReset && <ResetButtons />}

            {showProgress && (
              <CardOrganizationProgress activeStep={progressActiveStep} />
            )}

            <CardDataConfirmation npsn={currentNpsn} />

            <BannerUpdatedTargetPerilaku />

            <CardKinerjaStatus
              skpId={data?.data?.id}
              status={data?.data?.status}
              onSuccessUpdateSKP={refetchSKP}
              dataPelaksanaan={dataPelaksanaan?.data}
            />
            {isLoadingPelaksanaan && (
              <div className="w-full flex justify-center">
                <Spinner />
              </div>
            )}
          </>
        )}
        <CardKinerjaSteps />
      </div>
    </div>
  )
}

export default KinerjaKSIndexApp
